import {ReactElement, useRef, useState} from "react"
import {Button, Form, Steps} from "antd"
import {t} from "@ui/common/intl"
import {useStore} from "@ui/stores"
import {TBoatInfo} from "@ui/api/user"
import LeftArrowIcon from "@ui/icons/common/LeftArrow"
import Loader from "@ui/components/common/Loader"
import ConfirmationModal from "../common/modal/Confirmation"
import SuccessModal from "../common/modal/Success"
import ErrorModal from "../common/modal/Error"
import Panel from "../common/Panel"
import {isInfoEqual, ProfilePage, TProfilePageProps} from "../common"
import HintModal from "./common/modal/Hint"
import {TBoatInfoPageProps} from "./common"
import Dimensions from "./Dimensions"
import Tracking from "./Tracking"
import Common from "./Common"

type TInfoFactorytProps = {
  page: ProfilePage
}
const InfoFactory = ({page, ...rest}: TInfoFactorytProps & TBoatInfoPageProps) => {
  switch (page) {
    case ProfilePage.Common:
      return <Common {...rest} />
    case ProfilePage.Dimensions:
      return <Dimensions {...rest} />
    case ProfilePage.Tracking:
      return <Tracking {...rest} />
    default:
      return <></>
  }
}

const BoatInfo = ({page, setPage, close, isShow}: TProfilePageProps): ReactElement => {
  const {profile: {info: {boat = {}}, hasBoatInfo, updateBoat}} = useStore()
  const [changed, setChanged] = useState(false)
  const [hint, setHint] = useState<keyof TBoatInfo>()
  const [confirmExit, setConfirmExit] = useState(false)
  const [error, setError] = useState(false)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const stepsBoatInfo = useRef<TBoatInfo>({})
  const [form] = Form.useForm()

  const handleFinish = (values: Partial<TBoatInfo>) => {
    let info = values
    if (!hasBoatInfo) {
      stepsBoatInfo.current = {...stepsBoatInfo.current, ...values}
      if (page < ProfilePage.Tracking) {
        setPage(page + 1)
        return
      }
      info = stepsBoatInfo.current
    }

    setLoading(true)
    updateBoat(info)
      .then(() => setSuccess(true))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  const handleClose = () => {
    setPage(ProfilePage.Main)
  }

  const handleBack = () => {
    setPage(page - 1)
  }

  const handleShowHint = (key: keyof TBoatInfo) => {
    setHint(key)
  }

  const handleValueChanges = (changedValues: any, allValues: TBoatInfo) => {
    setChanged(!isInfoEqual(boat, {...boat, ...allValues}))
  }

  return isShow ? (
    <Panel
      className="boat-info-form"
      title={t("profile.boat.info.header")}
      onBack={() => changed ? setConfirmExit(true) : handleClose()}
      onClose={close}
    >
      {hasBoatInfo
        ? <div className="steps empty"></div>
        : <Steps
            className="steps"
            items={[{}, {}, {}]}
            direction="horizontal"
            responsive={false}
            current={page - ProfilePage.Common}
          />
      }
      {loading
        ? <Loader/>
        : <Form<Partial<TBoatInfo>>
            form={form}
            className="common"
            initialValues={boat}
            onFinish={handleFinish}
            onValuesChange={handleValueChanges}
          >
            <InfoFactory
              page={page}
              form={form}
              onShowHint={handleShowHint}
            />
            <div className="button-form-item">
              <div className="buttons">
                {page > ProfilePage.Common && !hasBoatInfo && (
                  <Button icon={<LeftArrowIcon />} onClick={handleBack}>{t("button.back")}</Button>
                )}
                <Button type="primary" htmlType="submit" disabled={!changed}>
                  {hasBoatInfo ? t("profile.boat.save.button.title") : t("profile.boat.next.button.title")}
                </Button>
              </div>
            </div>
          </Form>
      }
      <HintModal prop={hint} onClose={() => setHint(undefined)} />
      <ConfirmationModal
        text={t("profile.boat.info.edit.confirm.exit.text")}
        title={t("profile.boat.info.edit.confirm.exit.title")}
        onConfirm={handleClose}
        onClose={() => setConfirmExit(false)}
        show={confirmExit}
      />
      <ErrorModal onClose={() => setError(false)} show={error} />
      <SuccessModal
        onClose={() => {
          setSuccess(false)
          setPage(ProfilePage.Main)
        }}
        title={t("profile.boat.info.update.success.title")}
        show={success}
      />
    </Panel>
  ) : (<></>)
}

export default BoatInfo
